import React, { useRef, useState } from "react";
import { Braces, Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { AutomatedEmailVariable } from "@/services/organizationSettings";
import { copyTextToClipboard } from "@/utils/clipboard";

interface EmailVariablesPopoverProps {
  variables: AutomatedEmailVariable[];
  /** Puts the token at the editor's cursor; false when the editor cannot take it. */
  onInsert: (token: string) => boolean;
}

const HOLD_THRESHOLD_MS = 350;

/**
 * Reference card for template variables.
 *
 * A short click inserts a token at the editor's cursor and copies it. Holding or
 * selecting text deliberately does neither and keeps the card open, so
 * descriptions and tokens remain selectable.
 */
const EmailVariablesPopover: React.FC<EmailVariablesPopoverProps> = ({ variables, onInsert }) => {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState<"Inserted" | "Copied" | null>(null);
  const pointerDownAt = useRef(0);
  // Focus stays in the editor after an insert instead of returning to the trigger.
  const keepEditorFocus = useRef(false);

  const pickVariable = async (name: string) => {
    const token = `{${name}}`;
    // Copy before inserting: the clipboard fallback briefly focuses a hidden textarea.
    const copied = await copyTextToClipboard(token).then(
      () => true,
      () => false
    );
    keepEditorFocus.current = true;
    const inserted = onInsert(token);
    keepEditorFocus.current = inserted;
    // Neither worked: keep the selectable card open so the token can be copied manually.
    if (!inserted && !copied) return;
    setFeedback(inserted ? "Inserted" : "Copied");
    setOpen(false);
    window.setTimeout(() => setFeedback(null), 1500);
  };

  const handleClick = (name: string) => {
    const held =
      pointerDownAt.current > 0 && Date.now() - pointerDownAt.current >= HOLD_THRESHOLD_MS;
    pointerDownAt.current = 0;
    const selectedText = window.getSelection()?.toString().trim();
    if (held || selectedText) return;
    void pickVariable(name);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="outline">
          {feedback ? <Check className="h-4 w-4" /> : <Braces className="h-4 w-4" />}
          {feedback ?? "Variables"}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        // Phones: the card is the viewport width minus 2rem, so a 1rem collision padding
        // centres it with equal gutters; wider screens keep the end-aligned anchoring.
        collisionPadding={16}
        className="w-[min(36rem,calc(100vw-2rem))] max-w-none space-y-3 p-4"
        data-testid="email-variables-popover"
        onCloseAutoFocus={(event) => {
          if (keepEditorFocus.current) event.preventDefault();
          keepEditorFocus.current = false;
        }}
      >
        <div>
          <p className="text-sm font-medium">Available variables</p>
          <p className="text-xs text-muted-foreground">
            Click a variable to insert it at the cursor (it is copied too). Select any text normally
            by holding and dragging.
          </p>
        </div>
        <div className="grid max-h-80 gap-1.5 overflow-y-auto [scrollbar-gutter:stable_both-edges] sm:grid-cols-2">
          {variables.map((variable) => (
            <div
              key={variable.name}
              role="button"
              tabIndex={0}
              className="group flex cursor-copy select-text items-start justify-between gap-3 rounded-md border p-2.5 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onPointerDown={() => {
                pointerDownAt.current = Date.now();
              }}
              onClick={() => handleClick(variable.name)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                void pickVariable(variable.name);
              }}
            >
              <span className="min-w-0">
                <code className="text-xs font-medium text-foreground">{`{${variable.name}}`}</code>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {variable.description}
                  {variable.block ? " (formatted section)" : ""}
                </span>
              </span>
              <Copy className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EmailVariablesPopover;
